import React from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { WorkFilters, WorkStatus, WorkType } from "../../types/works";
import { WORK_STATUSES, WORK_TYPES } from "../../constants/worksConstants";

interface WorksFilterBarProps {
  filters: WorkFilters;
  onChange: (filters: WorkFilters) => void;
}

const chipBase =
  "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors whitespace-nowrap";
const chipActive = "bg-[#C97A3C] border-[#C97A3C] text-white";
const chipInactive = "bg-white border-[#E5E3DD] text-[#5A6460] hover:border-[#C97A3C] hover:text-[#C97A3C]";

export const WorksFilterBar: React.FC<WorksFilterBarProps> = ({ filters, onChange }) => {
  const { t } = useTranslation("works");

  const toggleType = (type: WorkType) => {
    const current = filters.type ?? [];
    const next = current.includes(type) ? current.filter((tp) => tp !== type) : [...current, type];
    onChange({ ...filters, type: next.length ? next : undefined });
  };

  const toggleStatus = (status: WorkStatus) => {
    const current = filters.status ?? [];
    const next = current.includes(status) ? current.filter((st) => st !== status) : [...current, status];
    onChange({ ...filters, status: next.length ? next : undefined });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="relative max-w-md">
        <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A9590]" />
        <input
          type="text"
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          placeholder={t("filters.searchPlaceholder")}
          className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-[#E5E3DD] bg-white text-[#2B2F2E] placeholder:text-[#8A9590] focus:outline-none focus:ring-2 focus:ring-[#C97A3C]/30 focus:border-[#C97A3C]"
        />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium text-[#8A9590] mr-0.5">{t("filters.type")}:</span>
        {WORK_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => toggleType(type)}
            className={`${chipBase} ${filters.type?.includes(type) ? chipActive : chipInactive}`}
          >
            {t(`type.${type}`)}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium text-[#8A9590] mr-0.5">{t("filters.status")}:</span>
        {WORK_STATUSES.map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => toggleStatus(status)}
            className={`${chipBase} ${filters.status?.includes(status) ? chipActive : chipInactive}`}
          >
            {t(`status.${status}`)}
          </button>
        ))}
      </div>
    </div>
  );
};
